import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { format } from 'date-fns';

const NotificationTimeline = ({ onClose, updateUnreadCount }) => {
  const [notifications, setNotifications] = useState([]);
  const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching notifications:', error);
      } else {
        setNotifications(data);
      }
    }
  };

  const markAsRead = async (notificationId) => {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId);

    if (error) {
      console.error('Error marking notification as read:', error);
    } else {
      setNotifications(notifications.map(notif =>
        notif.id === notificationId ? { ...notif, read: true } : notif
      ));
      
      // Update unread count if callback provided
      if (updateUnreadCount) {
        updateUnreadCount();
      }
    }
  };

  const markAllAsRead = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    setIsMarkingAllAsRead(true);
    
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', user.id)
      .eq('read', false);

    if (error) {
      console.error('Error marking all notifications as read:', error);
    } else {
      // Update local state
      setNotifications(notifications.map(notif => ({ ...notif, read: true })));
      
      // Update unread count if callback provided
      if (updateUnreadCount) {
        updateUnreadCount();
      }
    }
    
    setIsMarkingAllAsRead(false);
  };

  const unreadCount = notifications.filter(notif => !notif.read).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="fixed right-0 top-16 w-96 bg-white shadow-xl rounded-lg p-4 max-h-[80vh] overflow-y-auto"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center">
          <Bell className="mr-2" /> Notifications
        </h2>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            disabled={isMarkingAllAsRead}
            className="text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-3 py-1 rounded-full flex items-center transition-colors"
          >
            <CheckCheck className="mr-1" size={14} />
            {isMarkingAllAsRead ? 'Marking...' : 'Mark All as Read'}
          </button>
        )}
      </div>
      {notifications.length === 0 ? (
        <p>No notifications</p>
      ) : (
        <ul className="space-y-4">
          {notifications.map((notification) => (
            <li
              key={notification.id}
              className={`p-3 rounded-lg ${
                notification.read ? 'bg-gray-100' : 'bg-blue-100'
              }`}
            >
              <p className="font-semibold">{notification.message}</p>
              <p className="text-sm text-gray-600">
                {format(new Date(notification.created_at), 'PPpp')}
              </p>
              {!notification.read && (
                <button
                  onClick={() => markAsRead(notification.id)}
                  className="mt-2 text-blue-600 hover:text-blue-800 flex items-center"
                >
                  <Check className="mr-1" size={16} /> Mark as read
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <button
        onClick={onClose}
        className="mt-4 w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded"
      >
        Close
      </button>
    </motion.div>
  );
};

export default NotificationTimeline;
